import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../../lib/api';
import { MapPin, ArrowRight, Wrench, Building2, Calendar, Factory, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Breadcrumb, breadcrumbJsonLd } from '../../components/Breadcrumb';

export default function CityPage() {
  const { citySlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    publicAPI.getCityHub(citySlug)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [citySlug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(221,83%,22%)]"></div></div>;
  if (!data) return <div className="min-h-screen flex flex-col items-center justify-center"><h1 className="text-2xl font-semibold mb-4">Sehir bulunamadi</h1><Link to="/sehirler" className="text-[hsl(221,83%,22%)] underline">Sehirler</Link></div>;

  const { city, available_services, directory_companies, blog_posts, all_services } = data;
  const industrialContext = city.industrial_context || '';
  const industrialZones = city.industrial_zones || [];

  const breadcrumbItems = [
    { label: 'Ana Sayfa', href: '/' },
    { label: 'Sehirler', href: '/sehirler' },
    { label: city.name, href: `/${citySlug}` }
  ];

  return (
    <>
      <Helmet>
        <title>{city.meta_title || (city.name ? `${city.name} Endustriyel Hizmetler | Teklif Al` : 'Sehir | Uretim Noktasi')}</title>
        <meta name="description" content={city.meta_description || `${city.name} ilinde CNC isleme, lazer kesim ve kalip uretimi hizmetleri. Profesyonel endustriyel cozumler.`} />
        <meta property="og:title" content={`${city.name} Endustriyel Hizmetler`} />
        <meta property="og:description" content={city.meta_description || ''} />
        <link rel="canonical" href={`/${citySlug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Place",
          "name": city.name,
          "description": industrialContext || `${city.name} endustriyel uretim merkezi`,
          "address": { "@type": "PostalAddress", "addressLocality": city.name, "addressCountry": "TR" }
        })}</script>
        <script type="application/ld+json">{breadcrumbJsonLd(breadcrumbItems)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-[hsl(221,83%,22%)] text-white py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} variant="light" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="city-hub-title">
            {city.name} Endustriyel Hizmetler
          </h1>
          <p className="mt-4 text-lg text-blue-100 max-w-2xl">
            {city.name} ilinde CNC isleme, lazer kesim, kalip uretimi ve diger endustriyel uretim hizmetleri icin teklif alin.
          </p>
          <div className="mt-6">
            <Link to="/teklif-al">
              <Button size="lg" className="bg-white text-[hsl(221,83%,22%)] hover:bg-blue-50 font-semibold" data-testid="city-hub-cta-hero">
                Teklif Al <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 1: City Introduction */}
      {industrialContext && (
        <section className="py-12 bg-white" data-testid="city-intro-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold mb-6">{city.name} Sanayi Ekosistemi</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">{industrialContext}</p>
            {industrialZones.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Organize Sanayi Bolgeleri</h3>
                <div className="flex flex-wrap gap-2">
                  {industrialZones.map((zone, i) => (
                    <Badge key={i} variant="secondary" className="px-3 py-1.5 text-sm" data-testid={`zone-${i}`}>
                      <Factory className="h-3.5 w-3.5 mr-1.5" />
                      {zone}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Section 2: Available Services */}
      <section className="py-12 bg-slate-50" data-testid="city-services-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold mb-6">{city.name} - Mevcut Hizmetler</h2>
          {available_services.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {available_services.map((s, i) => (
                <Link key={i} to={s.url} data-testid={`city-service-${i}`}>
                  <Card className="h-full hover:shadow-md transition-shadow group">
                    <CardContent className="p-6">
                      <Wrench className="h-8 w-8 text-[hsl(221,83%,22%)] mb-3" />
                      <h3 className="font-semibold text-lg mb-2 group-hover:text-[hsl(221,83%,22%)] transition-colors">
                        {s.service_name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{s.service_summary}</p>
                      <span className="text-sm font-medium text-[hsl(221,83%,22%)] flex items-center gap-1">
                        Teklif Al <ArrowRight className="h-4 w-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {all_services.map(s => (
                <Card key={s.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <Wrench className="h-8 w-8 text-[hsl(221,83%,22%)] mb-3" />
                    <h3 className="font-semibold text-lg mb-2">{s.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{s.summary}</p>
                    <Link to="/teklif-al">
                      <Button variant="outline" size="sm" className="w-full">
                        Teklif Al <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Section 3: Directory Companies */}
      {directory_companies.length > 0 && (
        <section className="py-12 bg-white" data-testid="city-directory-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold">{city.name} Endustriyel Firmalar</h2>
              <Link to="/sanayi-rehberi" className="text-sm font-medium text-[hsl(221,83%,22%)] flex items-center gap-1 hover:underline">
                Tum Rehber <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {directory_companies.map((dc, i) => (
                <Link
                  key={dc.id}
                  to={`/sanayi-rehberi/${dc.category_slug}/${dc.slug}`}
                  data-testid={`city-dir-company-${i}`}
                >
                  <Card className="h-full hover:shadow-md transition-shadow group">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        {dc.logo ? (
                          <img src={dc.logo} alt={dc.name} className="h-10 w-10 object-contain rounded flex-shrink-0" loading="lazy" />
                        ) : (
                          <Building2 className="h-8 w-8 text-[hsl(221,83%,22%)] flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <h3 className="font-semibold group-hover:text-[hsl(221,83%,22%)] transition-colors truncate">
                            {dc.name}
                          </h3>
                          {dc.category_name && (
                            <Badge variant="secondary" className="text-xs mt-1">{dc.category_name}</Badge>
                          )}
                        </div>
                      </div>
                      {dc.description && (
                        <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{dc.description}</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section 4: Related Blog Posts */}
      {blog_posts.length > 0 && (
        <section className="py-12 bg-slate-50" data-testid="city-blog-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold">Endustriyel Uretim Rehberi</h2>
              <Link to="/blog" className="text-sm font-medium text-[hsl(221,83%,22%)] flex items-center gap-1 hover:underline">
                Tum Yazilar <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {blog_posts.slice(0, 3).map(post => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="h-full hover:shadow-md transition-shadow">
                    {post.cover_image && (
                      <img src={post.cover_image} alt={post.title} className="w-full h-40 object-cover rounded-t-xl" loading="lazy" />
                    )}
                    <CardContent className="p-5">
                      <h3 className="font-semibold mb-2 line-clamp-2">{post.title}</h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(post.created_at).toLocaleDateString('tr-TR')}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section 5: CTA */}
      <section className="py-16 bg-[hsl(221,83%,22%)] text-white" data-testid="city-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-semibold mb-4">
            {city.name} Uretim Projeniz Icin Teklif Alin
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            Proje detaylarinizi ve teknik cizimlerinizi gonderin, {city.name} ve cevresindeki uzman uretim firmalarindan teklif alin.
          </p>
          <Link to="/teklif-al">
            <Button size="lg" className="bg-white text-[hsl(221,83%,22%)] hover:bg-blue-50 font-semibold px-10" data-testid="city-cta-bottom">
              Teklif Al <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
}
